<template lang="pug">
.auth-wrapper.auth-v1.pa-0
  .auth-inner
    v-card.auth-card

      //- logo
      v-card-title.d-flex.align-center.justify-center.py-7
        router-link.d-flex.align-center(:to="home")
          v-img.me-3(
            :src="require(`@/assets/images/logos/logo.svg`)"
            max-height="30px"
            max-width="30px"
            alt="logo"
            contain
          )
          h2.text-2xl
            app-title

      //- title
      v-card-text
        center
          strong Olá, bem vindo! 👋🏻
          br
          span Por favor, entre com suas credenciais de usuário...

      //- login edit
      v-card-text
        v-form(
          ref="edit"
          style="min-height:270px"
          :disabled="authenticating"
          @submit.prevent="login"
        )
          widget.ma-0(
            ref="widget"
            v-for="(field,index) in fields"
            :key="`field-${index}`"
            :field="field"
            :context="context"
            outlined
            :value="field.value"
            @input="value => { $set(field, 'value', value); status = null }"
          )

          v-btn.mt-6(
            type="submit"
            block
            color="primary"
          )
            | {{ authenticating ? 'Autenticando...' : 'Autenticar' }}

          v-alert.mt-2(
            v-if="alert.type"
            text
            :type="alert.type"
            :color="alert.color"
          )
            p.font-weight-medium {{ alert.status }}

          v-alert.mt-2.font-weight-thin.text-xs(
            v-if="alert.message || alert.details.length"
            text
            type="warning"
            icon="mdi-bug"
          )
            p {{ alert.code }} - {{ alert.message }}
            p(
              v-for="(detail,index) in alert.details"
              :key="`detail-${index}`"
            )
              | {{ detail }}

      //- create new account
      template(v-if="allowCreateAccount")
        v-card-text.d-flex.align-center.justify-center.flex-wrap.mt-2
          span.me-2
            | Novo na plataedita?
          template
            router-link(:to="{name:'pages-register'}")
              | Crie uma conta

      //- social media
      template(v-if="allowSocialMedia")
        //- divider
        v-card-text.d-flex.align-center.mt-2
          v-divider
          span.mx-5 or
          v-divider

        //- social links
        v-card-actions.d-flex.justify-center
          v-btn.ms-1(
            v-for="link in socialLink"
            :key="link.icon"
            icon
          )
            v-icon(:color="$vuetify.theme.dark ? link.colorInDark : link.color")
              | {{ link.icon }}

</template>

<script>
// eslint-disable-next-line object-curly-newline
import { mdiFacebook, mdiTwitter, mdiGithub, mdiGoogle, mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'

import AppTitle from  "@/components/commons/layout/AppTitle"
import Widget from '@/components/widgets/Widget'
import { findRel } from "@/helpers/rels"
import { createStatus } from '@/helpers/entities'
import { STORE_IDENTITY } from '@/store/mutation-types'

export default {
  components: {
    AppTitle,
    Widget,
  },

  props: {
    site: String,
    eref: String,
    form: Object,
    data: Object,

    /** @type {Keep.Paper.Design.Entity} */
    entity: Object,

    /**
     * Instância de {Keep.Paper.Design.Link} para direcionamento do fluxo
     * depois de um login bem sucedido.
     */
    redirectLink: {
      type: Object,
      default: () => ({ eref:'Index.Workspace' })
    },
  },

  data: () => ({
    authenticating: false,
    isPasswordVisible: false,

    status: null,

    allowWelcomeMessage: false,
    allowCreateAccount: false,
    allowSocialMedia: false,

    icons: {
      mdiEyeOutline,
      mdiEyeOffOutline,
    },
    socialLink: [
      {
        icon: mdiFacebook,
        color: '#4267b2',
        colorInDark: '#4267b2',
      },
      {
        icon: mdiTwitter,
        color: '#1da1f2',
        colorInDark: '#1da1f2',
      },
      {
        icon: mdiGithub,
        color: '#272727',
        colorInDark: '#fff',
      },
      {
        icon: mdiGoogle,
        color: '#db4437',
        colorInDark: '#db4437',
      },
    ],
  }),

  computed: {
    home () {
      const site = this.$route.params.site
      return this.$to({ eref: 'Index.Workspace', site })
    },

    edit () {
      return this.entity.forms[0] || {}
    },

    links () {
      return this.edit.links || []
    },

    fields () {
      return this.edit.fields || []
    },

    targetSite () {
      return this.fields.filter(f => f.name.toLowerCase() === 'site')[0]?.value
    },

    username () {
      return this.fields.filter(f => f.name.toLowerCase() === 'username')[0]?.value
    },

    password () {
      return this.fields.filter(f => f.name.toLowerCase() === 'password')[0]?.value
    },

    row () {
      const sets = this.entity?.dataSet?.sets
      if (!sets) return {}

      const key = this.edit.set
      const set = key
        ? sets.filter(s => s.name === key)[0]
        : sets.filter(s => !s.name || s.name?.toLowerCase() === 'default')[0]
      if (!set || !set.rows) return {}

      return set.rows[0] || {}
    },

    alert () {
      if (!this.status) {
        return {
          type: null,
          color: null,
          code: null,
          status: null,
          message: null,
          details: []
        }
      }

      const entity = this.status.entity || {}

      if (entity.ok) {
        return {
          type: 'success',
          color: 'success',
          code: entity.code,
          status:'Autenticado!',
          message: null,
          details: []
        }
      }

      return {
        type: 'error',
        color: 'error',
        code: entity.code,
        status: entity.message || 'Erro ao autenticar.',
        
        message: null,
        details: []
      }
    },

    // [OBSOLETO] Estamos deixando de usar interpolação para usar expressão.
    context() {
      return {}
    },
  },

  watch: {
    row: "unwrapModel",
  },

  mounted() {
    this.unwrapModel();
  },

  methods: {
    async login () {
      let ok = this.$refs.edit.validate()
      
      if (!ok)
        return

      this.authenticating = true
      
      try {

        const link = findRel(this.links, { exact: 'Primary' })
        const { targetSite: site, username, password } = this
        const credential = { username, password }

        // autenticando...
        this.status = await this.$resolve(link, { site, credential })

        if (this.status.ok) {

          // salvando o TOKEN em cache...
          const { identity } = this.status.meta
          this.$store.commit(STORE_IDENTITY, identity)

          // redirecionando...
          await this.$browse(this.redirectLink)
        }

      } catch(error) {
        this.status = createStatus(this, error, this.redirectLink, 401, "Usuário e senha não conferem.")
      } finally {
        this.authenticating = false
      }
    },

    unwrapModel () {
      if (!this.entity || this.entity.unwrapped)
        return

      const { row, fields } = this
      fields.forEach(field => {
        const value = field.value || row[field.name]
        if (field.value !== value) {
          this.$set(field, "value", value);
        }
      })

      this.entity.unwrapped = true
    },
  },
}
</script>